// apps/share-web/src/song-screen/phone-screen.ts
//
// Телефонный экран песни (часть 1а редизайна, до 768 px). Сборка из модулей
// song-screen/* + волна (player-view/waves.ts) + поверхности чарта
// (player-view/chart-surfaces.ts). Экран стоит во весь вьюпорт поверх каркаса
// (position: fixed): шапка приложения, рельса и подвал на телефоне скрыты флагом
// body[data-pv-phone] — ✕ ведёт назад, ⏮ ⏭ — по сетлисту.
// Движок (player.ts) не меняется; экран только командует им.
import '@bandway/ui-components';
// Экран самодостаточен по стилям: компоненты ДС живут в светлом DOM и без своих
// внешних таблиц рендерятся голыми. Раньше их тянул только mount.ts (широкая
// раскладка), и экран, смонтированный без него, остался бы без капов и шкал.
import '@bandway/ui-components/styles/base.css';
import '@bandway/ui-components/styles/bw-button.css';
import '@bandway/ui-components/styles/bw-play-button.css';
import '@bandway/ui-components/styles/bw-rec-button.css';
import '@bandway/ui-components/styles/bw-volume-bar.css';
import '@bandway/ui-components/styles/bw-vu-meter.css';
import '@bandway/ui-components/styles/bw-transport.css';
import '@bandway/ui-components/styles/bw-segmented-control.css';
import '../player-view/player-view.css';
import '../styles/chart.css';
import type { PlayerViewDeps, PlayerViewHandle } from '../player-view/mount.js';
import { setState } from '../state.js';
import { mountWaves } from '../player-view/waves.js';
import { setlistNav } from '../player-view/setlist-nav.js';
import { createChartSurfaces, type ChartShared } from '../player-view/chart-surfaces.js';
import { getViewMemo, setViewMemo } from '../player-view/view-memo.js';
import { songMetaLine } from '../chart/chart-song.js';
import { createSongScreenStore } from './song-screen-store.js';
import { mountSongHead } from './song-head.js';
import { mountModeSwitch } from './mode-switch.js';
import { mountMixerPane, type MixerPaneHandle } from './mixer-pane.js';
import { mountPhoneTransport } from './phone-transport.js';
import { openActionSheet, closeActionSheet } from './action-sheet.js';
import { closeSong } from './close-song.js';
import { openFeedbackModal } from '../ui/feedback-modal.js';
import { t } from '../ui/i18n.js';
import '../styles/song-screen.css';

export interface PhoneScreenExtra {
  /** Швы для тестов ✕. */
  navigate?: (path: string) => void;
  search?: string;
  /** Тональность и колонки песни от mountPlayerView — переживают смену экрана на пороге 768 px. */
  chartShared?: ChartShared;
}

const box = (cls: string): HTMLDivElement => { const d = document.createElement('div'); d.className = cls; return d; };

export function mountPhoneScreen(host: Element, deps: PlayerViewDeps, extra: PhoneScreenExtra = {}): PlayerViewHandle {
  const { manifest, player } = deps;
  host.textContent = '';
  // Флаг прячет каркас приложения (шапка, рельса, подвал — song-screen.css, только ≤767 px). Размонтаж
  // его НЕ опускает: смена песни рвёт монтаж, затем ждёт сеть и только потом ставит следующий экран —
  // опущенный флаг вспыхивал бы старой шапкой на каждом ⏮ ⏭. Опускает тот, кто следующим показывает
  // каркас: широкая раскладка (mount.ts) и экран «слова без записи» (words-only-mount.ts).
  document.body.setAttribute('data-pv-phone', 'true');

  const nav = setlistNav(manifest.shareId);
  // Микс живёт в движке, а не в экране: поворот телефона через порог 768 px
  // пересобирает экран, а плеер остаётся. Стор, собранный из дефолтов манифеста,
  // затёр бы живой микс слушателя (mixer-pane толкает в движок то, что в сторе
  // отличается от движка) — поэтому собранные дорожки берут состояние у движка.
  // Master и петля так не восстанавливаются — их держит общая память экранов (view-memo.ts):
  // её пишут и этот экран, и широкая раскладка.
  const live = typeof player.getTrackStates === 'function' ? player.getTrackStates() : new Map();
  const store = createSongScreenStore({
    tracks: manifest.tracks.map((tr) => {
      const s = live.get(tr.id);
      return s ? { id: tr.id, initialVolume: s.gain, initialMuted: s.muted, initialSoloed: s.soloed } : tr;
    }),
  });
  const memo = getViewMemo(player);
  // Подписчиков ещё нет — set() здесь ничего не будит, а микшер Master на монтировании не пушит.
  store.set({ master: memo.master });
  // Master, выставленный раньше (на этом экране или на широком), экран и движок обязаны видеть
  // одинаково — толкаем его в движок ОДИН раз. Дефолт (1) не трогаем: у свежего плеера он и так
  // стоит, а лишний вызов не нужен.
  if (memo.master !== 1 && typeof player.setMasterGain === 'function') player.setMasterGain(Math.min(4, memo.master));
  const unsubMemo = store.subscribe((s, prev) => {
    if (s.master !== prev.master) setViewMemo(player, { master: s.master });
    if (s.loopSectionId !== prev.loopSectionId) setViewMemo(player, { loopSectionId: s.loopSectionId });
  });

  const root = box('ss-screen');
  const headHost = box('ss-head-host');
  const waveHost = box('ss-wave');
  const modeHost = box('ss-modes');
  const pane = box('ss-pane');
  const trHost = box('ss-transport-host');
  for (const el of [headHost, waveHost, modeHost, pane, trHost]) root.appendChild(el);
  host.appendChild(root);

  const meta = [
    songMetaLine(manifest.song),
    nav.position ? t('songScreen.position', { n: nav.position.n, total: nav.position.total }) : '',
  ].filter(Boolean).join(' · ');

  const head = mountSongHead(headHost, {
    title: manifest.song.name,
    meta,
    onClose: () => closeSong({ player, navigate: extra.navigate, search: extra.search }),
    onMore: () => openActionSheet({
      title: manifest.song.name,
      items: [
        { label: t('songScreen.share'), onSelect: () => setState({ shareDialog: { open: true } }) },
        { label: t('topbar.report'), onSelect: () => { openFeedbackModal(); } },
      ],
    }),
    mountVersionActions: deps.mountVersionActions,
  });

  const waves = mountWaves(waveHost, {
    manifest,
    peaks: deps.peaks,
    perTrackPeaks: deps.perTrackPeaks,
    player,
    showLanes: false,
    onLoopChange: (id) => store.set({ loopSectionId: id }),
  });
  // Волна при монтировании сбросила петлю движка (у свежей волны её нет) — вернуть ту,
  // что стояла до пересборки. Идёт ПОСЛЕ сброса, иначе сброс затёр бы восстановление.
  if (memo.loopSectionId) waves.setLoop(memo.loopSectionId);

  const surfaces = createChartSurfaces({
    manifest, player, nav,
    createFollow: deps.createFollow, onPlayGesture: deps.onPlayGesture, onNavigate: deps.onNavigate,
    shared: extra.chartShared,
  });
  const hasChart = surfaces !== null;

  let mixer: MixerPaneHandle | null = null;
  let panel: { destroy(): void } | null = null;
  const effectiveMode = (): 'mix' | 'chart' => (hasChart ? store.get().mode : 'mix');
  const renderPane = (): void => {
    mixer?.destroy(); mixer = null;
    panel?.destroy(); panel = null;
    pane.textContent = '';
    if (effectiveMode() === 'chart' && surfaces) {
      const p = surfaces.mountPanel(pane, { phone: true, onClose: () => store.set({ mode: 'mix' }) });
      p.setOpen(true);
      panel = p;
    } else {
      mixer = mountMixerPane(pane, { tracks: manifest.tracks, player, store });
    }
  };

  const modes = mountModeSwitch(modeHost, { store, hasChart, onMixMenu: () => mixer?.openMenu() });
  const transport = mountPhoneTransport(trHost, {
    player,
    durationSec: manifest.duration,
    sections: manifest.sections,
    store,
    hasPrev: !!nav.prevShareId,
    hasNext: !!nav.nextShareId,
    onPrev: nav.prevShareId ? () => deps.onNavigate?.(nav.prevShareId!) : undefined,
    onNext: nav.nextShareId ? () => deps.onNavigate?.(nav.nextShareId!) : undefined,
    onPlayGesture: deps.onPlayGesture,
    onRecord: () => setState({ promoModal: { open: true, feature: 'recorder' } }),
    onClearLoop: () => waves.setLoop(null),
  });

  // Дорожка, собранная позже: движок теряет setTrackState для ещё не собранной дорожки
  // (player.ts :590-593) и ждёт, что желаемое придёт с её load-progress. Эту подписку держит и
  // микшер, но в режиме Chart & Lyrics микшера нет — поэтому она живёт и на уровне экрана, пока
  // жив экран. Повторный пуск тех же значений безвреден (микшер в режиме Mix делает то же самое).
  const unsubLate = typeof player.subscribe === 'function'
    ? player.subscribe((ev) => {
      if (ev.type !== 'load-progress') return;
      const m = store.get().mix[ev.trackId];
      if (m) player.setTrackState(ev.trackId, { gain: m.gain, muted: m.muted, soloed: m.soloed });
    })
    : () => {};

  const unsubMode = store.subscribe((s, prev) => { if (s.mode !== prev.mode) renderPane(); });
  renderPane();
  surfaces?.restore(true);

  let raf = 0;
  const frame = (): void => {
    // Следующий кадр — ПЕРВЫМ: брошенное синхронизацией исключение не должно убить цикл
    // (иначе транспорт и VU замерли бы до конца сессии), а id нужен unmount-у сразу.
    raf = requestAnimationFrame(frame);
    transport.sync(player.getPlayhead(), player.isPlaying());
    mixer?.tick();
  };
  if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(frame);

  // load() мемоизирован в player.ts: широкий и телефонный экран делят один пул.
  void player.load();

  let done = false;
  return {
    unmount() {
      if (done) return;                                           // повторный unmount — не операция
      done = true;
      if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      raf = 0;
      unsubMode();
      unsubMemo();
      unsubLate();
      mixer?.destroy(); mixer = null;
      panel?.destroy(); panel = null;
      surfaces?.destroy();
      transport.destroy();
      modes.destroy();
      waves.unmount();
      head.destroy();
      closeActionSheet();                                        // лист шапки не должен пережить экран (микшера в режиме Chart нет)
      root.remove();
    },
  };
}
